/**
 * The authenticated app shell: a top bar (wordmark · tenant switcher · env ·
 * theme · sign out) and a persistent left sidebar (Rooms · Alerts), with a
 * scrollable content area. All surfaces are token-styled so the shell themes for
 * light and dark; the controls are Oxygen components. The tenant switcher is hidden
 * in 'fixed' mode (the operator is locked to their own tenant).
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import styled from 'styled-components';
import { IconButton } from '@8x8/oxygen-button';
import { BellAlertIcon, CalendarIcon, DevicesIcon, LeaveIcon, LightbulbIcon } from '@8x8/oxygen-icon';
import type { Environment } from '@spaces/contracts';
import { getHealthSummary } from '../api';
import { navigate, useLocation } from '../router';
import type { ThemeMode } from '../theme';
import { EnvTag } from './primitives';
import { Logo } from './Logo';

const Frame = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${p => p.theme.ui31};
    color: ${p => p.theme.textColor01};
`;

const TopBar = styled.header`
    height: 52px;
    flex-shrink: 0;
    background: ${p => p.theme.ui25};
    border-bottom: 1px solid ${p => p.theme.ui01};
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    z-index: 30;
`;

const TopLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Wordmark = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${p => p.theme.textColor01};
    font-size: 16px;
    font-weight: 600;
`;

const Divider = styled.div`
    width: 1px;
    height: 24px;
    background: ${p => p.theme.ui01};
    margin: 0 8px;
`;

const TenantButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: none;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    background: transparent;
    color: ${p => p.theme.textColor01};
    font-size: 13px;

    &:hover {
        background: ${p => p.theme.ui02};
    }
`;

const TenantDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${p => p.theme.action01};
`;

const TopRight = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SignOut = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 6px;
    background: transparent;
    color: ${p => p.theme.textColor02};
    font-size: 13px;
    font-weight: 600;

    &:hover {
        background: ${p => p.theme.ui02};
        color: ${p => p.theme.textColor01};
    }
`;

const Who = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.theme.textColor02};
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Body = styled.div`
    flex: 1;
    display: flex;
    min-height: 0;
`;

const Sidebar = styled.aside`
    width: 216px;
    flex-shrink: 0;
    background: ${p => p.theme.ui25};
    border-right: 1px solid ${p => p.theme.ui01};
    display: flex;
    flex-direction: column;
    padding: 12px 8px;
`;

const NavItem = styled.button<{ $active: boolean }>`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    border: none;
    cursor: pointer;
    padding: 8px 10px;
    border-radius: 6px;
    text-align: left;
    font-size: 14px;
    background: ${p => (p.$active ? `${p.theme.action08}14` : 'transparent')};
    color: ${p => (p.$active ? p.theme.action08 : p.theme.textColor01)};
    font-weight: ${p => (p.$active ? 600 : 400)};

    &:hover {
        background: ${p => (p.$active ? `${p.theme.action08}14` : p.theme.ui02)};
    }
`;

const NavBadge = styled.span<{ $alarm?: boolean }>`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 1000px;
    background: ${p => (p.$alarm ? p.theme.error01 : p.theme.ui32)};
    color: ${p => (p.$alarm ? p.theme.ui06 : p.theme.textColor02)};
    font-size: 11px;
    font-weight: 700;
`;

const SidebarFooter = styled.div`
    margin-top: auto;
    padding-top: 10px;
    border-top: 1px solid ${p => p.theme.ui01};
    font-size: 11px;
    color: ${p => p.theme.textColor05};
    padding-left: 10px;
`;

const Content = styled.main`
    flex: 1;
    min-width: 0;
    overflow-y: auto;
`;

interface ShellProps {
    env: Environment;
    tenant: string | null;
    /** The signed-in operator (their 8x8 login id), shown in the top bar. */
    identity: string | null;
    themeMode: ThemeMode;
    onToggleTheme: () => void;
    onSignOut: () => void;
    children: ReactNode;
}

/** Live count of active alerts for the current tenant (drives the sidebar badge). */
function useActiveAlertCount(tenant: string | null): number | null {
    const [count, setCount] = useState<number | null>(null);
    const reqId = useRef(0);

    useEffect(() => {
        if (!tenant) {
            setCount(null);

            return;
        }

        const load = (): void => {
            const id = ++reqId.current;

            void getHealthSummary(tenant).then(result => {
                if (id === reqId.current) {
                    setCount(result.ok ? result.data.activeAlerts : null);
                }
            });
        };

        load();
        const t = setInterval(load, 20_000);

        return () => clearInterval(t);
    }, [tenant]);

    return count;
}

export function Shell(props: ShellProps): JSX.Element {
    const route = useLocation();
    const roomsActive = route.name === 'rooms' || route.name === 'create' || route.name === 'detail';
    const alertsActive = route.name === 'alerts';
    const calendarsActive = route.name === 'calendars';
    const alertCount = useActiveAlertCount(props.tenant);

    return (
        <Frame>
            <TopBar>
                <TopLeft>
                    <Wordmark>
                        <Logo height={20} />
                        <span>Rooms Admin</span>
                    </Wordmark>
                    <Divider />
                    <TenantButton as="div" title="Tenant (from your 8x8 token)" style={{ cursor: 'default' }}>
                        <TenantDot />
                        <span style={{ fontWeight: 600 }}>{props.tenant ?? 'No tenant'}</span>
                    </TenantButton>
                </TopLeft>
                <TopRight>
                    {props.identity ? <Who title="Signed in operator">{props.identity}</Who> : null}
                    <IconButton onClick={props.onToggleTheme} title="Toggle light / dark theme">
                        <LightbulbIcon size={18} />
                    </IconButton>
                    <EnvTag env={props.env} />
                    <SignOut onClick={props.onSignOut}>
                        <LeaveIcon size={16} />
                        Sign out
                    </SignOut>
                </TopRight>
            </TopBar>
            <Body>
                <Sidebar>
                    <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <NavItem $active={roomsActive} onClick={() => navigate('/rooms')}>
                            <DevicesIcon size={20} />
                            <span>Rooms</span>
                        </NavItem>
                        <NavItem $active={alertsActive} onClick={() => navigate('/alerts')}>
                            <BellAlertIcon size={20} />
                            <span>Alerts</span>
                            {alertCount !== null && alertCount > 0 ? (
                                <NavBadge $alarm>{alertCount}</NavBadge>
                            ) : null}
                        </NavItem>
                        <NavItem $active={calendarsActive} onClick={() => navigate('/calendars')}>
                            <CalendarIcon size={20} />
                            <span>Calendars</span>
                        </NavItem>
                    </nav>
                    <SidebarFooter>v0.1 · ops console</SidebarFooter>
                </Sidebar>
                <Content>{props.children}</Content>
            </Body>
        </Frame>
    );
}
